/**
 * DocumentComparisonLayout
 *
 * A reusable multi-column layout component for comparing documents with extracted data.
 * Supports 2-4 resizable columns with independent scrolling.
 */

import { useEffect, useMemo } from 'react';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { ColumnConfig } from './columns/types';
import { isDocumentColumn, isDataColumn } from './columns/types';
import { DocumentViewerColumn } from './columns/DocumentViewerColumn';
import { useDocumentComparison } from './hooks/useDocumentComparison';

interface DocumentComparisonLayoutProps {
  /** Column configurations (2-4 columns supported) */
  columns: ColumnConfig[];
  /** Additional CSS classes for the container */
  className?: string;
  /** Callback when ESC key is pressed - used to close the layout */
  onClose?: () => void;
}

/**
 * Calculate minimum panel size as a percentage based on pixel min width.
 * Assumes a typical viewport width for calculation.
 */
function calculateMinSize(minWidthPx: number | undefined): number {
  if (!minWidthPx) return 15; // Default minimum 15%
  // Rough calculation assuming ~1200px container width
  // The actual constraint is enforced by the panel itself
  return Math.max(15, Math.min(40, (minWidthPx / 1200) * 100));
}

export function DocumentComparisonLayout({
  columns,
  className,
  onClose,
}: DocumentComparisonLayoutProps) {
  const context = useDocumentComparison();

  useEffect(() => {
    if (!onClose) return;

    const closeHandler = onClose;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        closeHandler();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Validate column count
  if (columns.length < 2 || columns.length > 4) {
    console.warn(
      `DocumentComparisonLayout: Expected 2-4 columns, got ${columns.length}`,
    );
  }

  // Calculate default sizes for columns
  const defaultSizes = useMemo(() => {
    const withDefaults = columns.map((col) => col.defaultWidthPercent);
    const hasAllDefaults = withDefaults.every((d) => d !== undefined);

    if (hasAllDefaults) {
      return withDefaults as number[];
    }

    // Distribute evenly if not all specified
    const evenSize = 100 / columns.length;
    return columns.map(() => evenSize);
  }, [columns]);

  return (
    <div className={cn('h-full w-full px-3 pb-3 bg-muted/20', className)}>
      <ResizablePanelGroup direction="horizontal" className="h-full gap-[3px]">
        {columns.map((column, index) => (
          <ColumnPanel
            key={column.id}
            column={column}
            defaultSize={defaultSizes[index]}
            isLast={index === columns.length - 1}
            context={context}
          />
        ))}
      </ResizablePanelGroup>
    </div>
  );
}

interface ColumnPanelProps {
  column: ColumnConfig;
  defaultSize: number;
  isLast: boolean;
  context: ReturnType<typeof useDocumentComparison>;
}

function ColumnPanel({
  column,
  defaultSize,
  isLast,
  context,
}: ColumnPanelProps) {
  const minSize = calculateMinSize(column.minWidthPx);
  const isDocColumn = isDocumentColumn(column);

  return (
    <>
      <ResizablePanel
        defaultSize={defaultSize}
        minSize={minSize}
        className="flex flex-col rounded-lg border border-gray-300 bg-background shadow-sm overflow-hidden"
      >
        {/* Column Header */}
        <div className="flex-shrink-0 border-b border-border bg-muted/30 px-4 py-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            {column.title}
          </h3>
        </div>

        {/* Column Content - Document columns handle their own scrolling */}
        {isDocColumn ? (
          <div className="flex-1 min-h-0 w-full overflow-hidden">
            <ColumnContent column={column} context={context} />
          </div>
        ) : (
          <div className="flex-1 min-h-0 w-full">
            <ScrollArea className="h-full w-full">
              <ColumnContent column={column} context={context} />
            </ScrollArea>
          </div>
        )}
      </ResizablePanel>

      {/* Resize Handle (except after last column) */}
      {!isLast && <ResizableHandle withHandle />}
    </>
  );
}

interface ColumnContentProps {
  column: ColumnConfig;
  context: ReturnType<typeof useDocumentComparison>;
}

function ColumnContent({ column, context }: ColumnContentProps) {
  if (isDocumentColumn(column)) {
    return <DocumentViewerColumn config={column} />;
  }

  if (isDataColumn(column)) {
    const renderContext = {
      hoveredFieldPath: context.hoveredFieldPath,
      lockedFieldPath: context.lockedFieldPath,
      onFieldHover: context.setHoveredField,
      onFieldClick: context.setLockedField,
      getCitationsForField: context.getCitationsForField,
      getConfidenceForField: context.getConfidenceForField,
    };

    return <div className="p-4">{column.render(renderContext)}</div>;
  }

  // Custom column
  if (column.type === 'custom') {
    return <div className="p-4">{column.render()}</div>;
  }

  return null;
}
